import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useContext, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Color } from '@/constants/color';
import emptySelectOption from '@/constants/emptySelectOption';
import { TagSlugs } from '@/constants/tagSlugs';
import AppContext from '@/contexts/appContext';
import FieldError from '@/forms/FieldError';
import useOrganizations from '@/hooks/useOrganizations';
import usePeople from '@/hooks/usePeople';
import usePerson from '@/hooks/usePerson';
import ReferralRepository from '@/repositories/ReferralRepository';
import { DIRECTION } from '@/types/sort';
import AutocompleteSingleSelect from '@/ui/components/AutocompleteSingleSelect';
import Button from '@/ui/components/Button';
import Card from '@/ui/components/Card';
import InputField from '@/ui/components/Form/InputField';
import InputGroup from '@/ui/components/Form/InputGroup';
import RadioGroup from '@/ui/components/Form/RadioGroup';
import { OptionType } from '@/ui/components/Form/Select/types';
import Textarea from '@/ui/components/Form/Textarea';
import Heading from '@/ui/components/Heading';
import PageHeaderBar from '@/ui/components/PageHeaderBar';
import Spinner from '@/ui/components/Spinner';
import Tooltip from '@/ui/components/Tooltip';
import { toIsoString } from '@/utils/dates';
import formatOrganizationToSelectOption from '@/utils/formatOrganizationToSelectOption';
import { formatPersonToSelectOption } from '@/utils/formatPersonToSelectOption';
import mapAxiosErrorMessages from '@/utils/mapAxiosErrorMessages';

import { ReferralFormValues } from './types';

export const REFERRER_ROLE_OPTIONS: OptionType<string>[] = [
  {
    label: 'Medical Provider',
    value: 'Medical Provider'
  },
  {
    label: 'Case Worker',
    value: 'Case Worker'
  },
  {
    label: 'Lawyer',
    value: 'Lawyer'
  },
  {
    label: 'Parole Officer',
    value: 'Parole Officer'
  },
  {
    label: 'Friend/Family',
    value: 'Friend/Family'
  },
  {
    label: 'Other',
    value: 'Other'
  }
];

const VIA_OPTIONS: OptionType<string>[] = [
  {
    label: 'Phone',
    value: 'Phone'
  },
  {
    label: 'Email',
    value: 'Email'
  },
  {
    label: 'In Person',
    value: 'In Person'
  },
  {
    label: 'Portal',
    value: 'Portal'
  }
];

interface ReferralFormProps {
  referralId?: string;
  onSuccess?: () => void;
}
export const myPeopleDefaultSearchParams = {
  direction: DIRECTION.DESC,
  ordering: 'created_at',
  page: '1',
  search: ''
};

const ReferralForm: React.FC<ReferralFormProps> = ({
  referralId,
  onSuccess
}: ReferralFormProps) => {
  const { addMessage } = useContext(AppContext);
  const navigate = useNavigate();
  const [peopleOptions, setPeopleOptions] = useState<OptionType<string>[]>([]);
  const [organizationOptions, setOrganizationOptions] = useState<OptionType<string>[]>([]);

  const [searchParams] = useSearchParams();
  const [personSearch, setPersonSearch] = useState('');
  const [orgSearch, setOrgSearch] = useState('');
  const {
    usePersonQuery: { data: personResponse }
  } = usePerson({
    id: searchParams.get('personId') || '',
    enabled: !!searchParams.get('personId')
  });
  const activeParams = {
    direction: searchParams.get('direction') ?? myPeopleDefaultSearchParams.direction,
    ordering: searchParams.get('ordering') ?? myPeopleDefaultSearchParams.ordering,
    page: searchParams.get('page') ?? myPeopleDefaultSearchParams.page,
    search: searchParams.get('search') ?? myPeopleDefaultSearchParams.search
  };
  const { direction, ordering, page } = activeParams;
  const { data, isLoading } = usePeople({
    params: {
      ordering: `${direction === DIRECTION.ASC ? '' : '-'}${ordering}`,
      page,
      tag_slugs: [TagSlugs.REFERRER_NOT_A_LEAD],
      ...(personSearch && { search: personSearch })
    },
    enabled: !!personSearch
  });

  const { data: orgData, isLoading: isLoadingOrgs } = useOrganizations({
    params: {
      page,
      ...(orgSearch && { search: orgSearch })
    },
    enabled: !!orgSearch
  });

  const referralRepository = ReferralRepository();

  const defaultValues: ReferralFormValues = {
    reason: '',
    referred_by: emptySelectOption,
    referred_by_organization: emptySelectOption,
    reference_for: emptySelectOption,
    referred_at: toIsoString(new Date()).slice(0, 16),
    source: '',
    role_of_referrer: '',
    via: ''
  };

  const {
    control,
    formState: { errors },
    getValues,
    setValue,
    handleSubmit,
    reset
  } = useForm<ReferralFormValues>({ defaultValues });
  const [selectedVersion, setSelectedVersion] = useState(0);

  useEffect(() => {
    if (personResponse?.data) {
      setValue('reference_for', formatPersonToSelectOption(personResponse.data));
    }

    return () => {};
  }, [personResponse, setValue]);

  useEffect(() => {
    const searchPeopleOptions = data?.data?.results?.map(formatPersonToSelectOption) || [];
    const searchOrgOptions = orgData?.data?.results?.map(formatOrganizationToSelectOption) || [];
    const values = getValues();

    const findOrPushOption = (option: OptionType<string> | null | undefined) => {
      if (option) {
        const existingOption = searchPeopleOptions.find(
          (searchPeopleOption) => searchPeopleOption.value === option.value
        );
        if (!existingOption && option.value !== emptySelectOption.value) {
          searchPeopleOptions.push(option);
        }
      }
    };

    findOrPushOption(values.referred_by);
    findOrPushOption(values.reference_for);

    const orgSelected = values.referred_by_organization;
    if (
      orgSelected &&
      orgSelected.value !== emptySelectOption.value &&
      !searchOrgOptions.find((opt) => opt.value === orgSelected.value)
    ) {
      searchOrgOptions.push(orgSelected);
    }

    setPeopleOptions(searchPeopleOptions);
    setOrganizationOptions(searchOrgOptions);

    return () => {};
  }, [
    data,
    orgData,
    setPeopleOptions,
    setOrganizationOptions,
    getValues,
    personResponse,
    selectedVersion
  ]);

  const createReferral = useMutation({
    mutationFn: (values: ReferralFormValues) =>
      referralRepository.createReferral({
        reason: values.reason,
        referred_by_id: values.referred_by?.value || undefined,
        referred_by_organization_id: values.referred_by_organization?.value || undefined,
        reference_for_id: values.reference_for?.value || undefined,
        referred_at: new Date(values.referred_at).toISOString(),
        source: values.source,
        role_of_referrer: values.role_of_referrer,
        via: values.via
      }),
    onSuccess: () => {
      addMessage({ level: 'success', text: 'Successfully created referral' });
      onSuccess && onSuccess();
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
    }
  });

  const updateReferral = useMutation({
    mutationFn: (values: ReferralFormValues) =>
      referralRepository.updateReferral({
        id: values.id as string,
        reason: values.reason,
        referred_by_id: values.referred_by?.value || undefined,
        referred_by_organization_id: values.referred_by_organization?.value || undefined,
        reference_for_id: values.reference_for?.value || undefined,
        referred_at: new Date(values.referred_at).toISOString(),
        source: values.source,
        role_of_referrer: values.role_of_referrer,
        via: values.via
      }),
    onSuccess: () => {
      addMessage({ level: 'success', text: 'Successfully updated referral' });
      onSuccess && onSuccess();
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
    }
  });

  useEffect(() => {
    if (referralId) {
      const fetchReferral = async () => {
        const referral = await referralRepository.getReferral(referralId);
        if (referral.isError) {
          return;
        }
        if (!referral.data) {
          return;
        }
        reset({
          id: referral.data.id,
          reason: referral.data.reason,
          referred_by: formatPersonToSelectOption(referral.data.referred_by),
          referred_by_organization: formatOrganizationToSelectOption(
            referral.data.referred_by_organization
          ),
          reference_for: formatPersonToSelectOption(referral.data.reference_for),
          referred_at: toIsoString(new Date(referral.data.referred_at)).slice(0, 16),
          source: referral.data.source,
          role_of_referrer: referral.data.role_of_referrer,
          via: referral.data.via
        });
        setSelectedVersion((v) => v + 1);
      };
      fetchReferral();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referralId, reset]);

  const onSubmit = (values: ReferralFormValues) => {
    const hasReferrerPerson = !!values.referred_by?.value;
    const hasReferrerOrg = !!values.referred_by_organization?.value;
    if (!hasReferrerPerson && !hasReferrerOrg) {
      addMessage({ level: 'error', text: 'You must select a Referrer (person or organization).' });
      return;
    }
    if (referralId) {
      updateReferral.mutate(values);
    } else {
      createReferral.mutate(values);
    }
  };

  const handlePersonLookup = async (search: string) => {
    setPersonSearch(search);
  };

  const handleOrganizationLookup = async (search: string) => {
    setOrgSearch(search);
  };

  return (
    <Card>
      <PageHeaderBar>
        <Heading level={1}>{referralId ? 'Update Referral' : 'Create Referral'}</Heading>
      </PageHeaderBar>
      <form onSubmit={handleSubmit(onSubmit)}>
        <InputGroup>
          <Controller
            control={control}
            name="referred_by"
            render={({ field: { onChange, value } }) => (
              <>
                <AutocompleteSingleSelect
                  clearable={true}
                  label={
                    <>
                      Referred By {isLoading && <Spinner className="ml-2" />} or{' '}
                      <a href="/people/new" className="text-blue-600 text-sm" target="_blank">
                        + Create New
                      </a>
                    </>
                  }
                  onChange={onChange}
                  onSearch={handlePersonLookup}
                  options={peopleOptions}
                  placeholder="ex. Dr. Otto Gunther Octavius"
                  selectedOption={
                    peopleOptions.find((option) => option.value === value?.value) ||
                    emptySelectOption
                  }
                />
                {value?.value && (
                  <a
                    href={`/people/${value?.value}`}
                    className="text-blue-600 text-sm"
                    target="_blank"
                    rel="noreferrer">
                    View Profile
                  </a>
                )}
              </>
            )}
          />
          <FieldError message={errors.referred_by?.message} />
        </InputGroup>

        <InputGroup>
          <Controller
            control={control}
            name="referred_by_organization"
            render={({ field: { onChange, value } }) => (
              <>
                <AutocompleteSingleSelect
                  clearable={true}
                  label={
                    <>
                      Referred By Organization {isLoadingOrgs && <Spinner className="ml-2" />}
                      <Tooltip
                        placement="right"
                        tooltipContent={
                          <span>
                            Organizations with Referral Configs and enabled automatic ROI generation
                            will generate a ROI
                          </span>
                        }>
                        <span className="inline-flex ml-2 align-middle cursor-help text-gray-500">
                          <FontAwesomeIcon icon={faInfoCircle} />
                        </span>
                      </Tooltip>
                    </>
                  }
                  onChange={onChange}
                  onSearch={handleOrganizationLookup}
                  options={organizationOptions}
                  missingOptionsText="Start typing or try another search query to see results..."
                  placeholder="ex. Octavius Industries"
                  selectedOption={
                    organizationOptions.find((option) => option.value === value?.value) ||
                    emptySelectOption
                  }
                />
              </>
            )}
          />
          <FieldError message={errors.referred_by_organization?.message as string} />
        </InputGroup>

        <InputGroup>
          <Controller
            control={control}
            name="reference_for"
            render={({ field: { onChange, value } }) => (
              <>
                <AutocompleteSingleSelect
                  clearable={true}
                  label={
                    <span>
                      Reference For {isLoading && <Spinner className="ml-2" />} or{' '}
                      <a href="/people/new" className="text-blue-600 text-sm" target="_blank">
                        + Create New
                      </a>
                    </span>
                  }
                  onChange={onChange}
                  onSearch={handlePersonLookup}
                  options={peopleOptions}
                  placeholder="ex. Peter Parker"
                  selectedOption={
                    peopleOptions.find((option) => option.value === value?.value) ||
                    emptySelectOption
                  }
                />
                {value?.value && (
                  <a
                    href={`/people/${value?.value}`}
                    className="text-blue-600 text-sm"
                    target="_blank"
                    rel="noreferrer">
                    View Profile
                  </a>
                )}
              </>
            )}
          />
          <FieldError message={errors.reference_for?.message} />
        </InputGroup>

        <InputGroup>
          <Controller
            control={control}
            name="reason"
            render={({ field }) => (
              <Textarea
                labelText={'Reason for Referral'}
                id="reason"
                name={field.name}
                onChange={field.onChange}
                onBlur={field.onBlur}
                value={field.value}
                placeholder="eg., He was bit by a spider"
              />
            )}
          />
          <FieldError message={errors.reason?.message} />
        </InputGroup>

        <InputGroup>
          <Controller
            control={control}
            name="referred_at"
            render={({ field }) => (
              <InputField label="Referred On" id="referred_at" type="datetime-local" {...field} />
            )}
          />
          <FieldError message={errors.referred_at?.message} />
        </InputGroup>

        <InputGroup>
          <Controller
            control={control}
            name="source"
            render={({ field }) => (
              <InputField label="Source / Organization" type="text" id="source" {...field} />
            )}
          />
          <FieldError message={errors.source?.message} />
        </InputGroup>

        <InputGroup>
          <Controller
            control={control}
            name="role_of_referrer"
            render={({ field }) => (
              <RadioGroup
                id="via"
                label="Role"
                options={REFERRER_ROLE_OPTIONS}
                selectedValue={field.value}
                onChange={field.onChange}
              />
            )}
          />
          <FieldError message={errors.role_of_referrer?.message} />
        </InputGroup>

        {/* Only show this section if we're creating a new referral */}
        {!referralId && (
          <InputGroup>
            <Controller
              control={control}
              name="via"
              render={({ field }) => (
                <RadioGroup
                  id="via"
                  label="Via"
                  options={VIA_OPTIONS}
                  selectedValue={field.value}
                  onChange={field.onChange}
                />
              )}
            />
            <FieldError message={errors.via?.message} />
          </InputGroup>
        )}

        <div className="flex gap-2">
          <Button disabled={createReferral.isPending || updateReferral.isPending} type="submit">
            {createReferral.isPending || updateReferral.isPending ? (
              <Spinner color={Color.WHITE} label="Submitting..." />
            ) : referralId ? (
              'Update Referral'
            ) : (
              'Create Referral'
            )}
          </Button>

          <Button color={Color.WHITE} type="button" onClick={() => navigate(-1)}>
            Cancel
          </Button>
        </div>
      </form>
    </Card>
  );
};

export default ReferralForm;
